/**
 * Asymmetric-margin op. A NEW structure from SIX explicit signed cm
 * per-direction offsets in the patient anatomical (LPS) frame. The coordinate
 * frame is the slice-specific dominant hazard: a swapped or
 * negated axis is a clinical geometry error, so the six named directions are
 * mapped to the two engine vectors ENCAPSULATED here, once, at the single
 * shared cm→mm choke-point, and golden-tested per direction:
 *
 *   negLpsMm = [ rightCm , anteriorCm , inferiorCm  ] · cmToMmChokepoint
 *   posLpsMm = [ leftCm  , posteriorCm, superiorCm  ] · cmToMmChokepoint
 *
 * Positive = expand outward, negative = contract inward, 0 = no change.
 */
import {
    cmToMmChokepoint,
    resolveSourceForNewStructure,
    previewSingleCall,
    commitComputed,
    MAX_ABS_MARGIN_CM,
    type Brand,
    type Mm,
    type ComputedGeometry,
    type OpDeps,
    type ValidateResult,
    type CommitResult,
} from '../core';
import type { ApplyAsymmetricMarginInput } from '../../contract';

export type AsymMarginInput = ApplyAsymmetricMarginInput;

/**
 * A patient-frame margin triple in mm, in the EXACT slot order
 * `SDFOperations.applyAsymmetricMargin` consumes:
 * `negLpsMm = [Right, Anterior, Inferior]`, `posLpsMm = [Left, Posterior,
 * Superior]`. Only the cm→mm choke-point can mint the branded `Mm` components,
 * so a raw number cannot reach the engine port and the axis order is fixed
 * at the single audited boundary.
 */
export type LpsMarginMm = readonly [Mm, Mm, Mm];

export type AsymMarginDeps = OpDeps<ComputedGeometry> & {
    readonly geometry: {
        applyAsymmetricMargin(
            sourceRoiNumber: number,
            negLpsMm: LpsMarginMm,
            posLpsMm: LpsMarginMm
        ): Promise<ComputedGeometry>;
    };
};

/**
 * The six explicit signed cm direction field names, the SINGLE source shared
 * by the numeric/all-zero guards and the input parser so a field rename
 * cannot drift between validation and parsing (a swapped/dropped direction is
 * the slice's dominant hazard).
 */
export const ASYM_DIRECTION_KEYS = [
    'superiorCm',
    'inferiorCm',
    'anteriorCm',
    'posteriorCm',
    'leftCm',
    'rightCm',
] as const satisfies ReadonlyArray<keyof AsymMarginInput>;

/* ── Stage 1: resolve (target + units + frame mapping + invariants) ── */

interface AsymMarginValidatedData {
    readonly sourceRoiNumber: number;
    readonly sourceVolumeCm3: number;
    /** Locked LPS slot order; minted at the single cm→mm choke-point. */
    readonly negLpsMm: LpsMarginMm;
    readonly posLpsMm: LpsMarginMm;
    readonly newRoi: string;
}
/** Only `resolveAsymmetricMargin` mints this (the brand is unforgeable
 *  without an explicit cast) — preview cannot be skipped. */
export type AsymMarginValidated = Brand<
    AsymMarginValidatedData,
    'AsymMarginValidated'
>;

export function resolveAsymmetricMargin(
    input: AsymMarginInput,
    deps: AsymMarginDeps
): ValidateResult<AsymMarginValidated> {
    // Fail-fast on the pure, dependency-free numeric invariants first. The
    // guard messages use the model-facing field name (single source:
    // ASYM_DIRECTION_KEYS) so a re-ask is unambiguous.
    const dirs = ASYM_DIRECTION_KEYS.map((k) => [k, input[k]] as const);
    for (const [name, v] of dirs) {
        if (!Number.isFinite(v)) {
            return {
                kind: 'invalid',
                reason: `${name} must be a finite number of centimetres`,
            };
        }
        if (Math.abs(v) > MAX_ABS_MARGIN_CM) {
            return {
                kind: 'invalid',
                reason:
                    `${name} ${v} cm is outside the safe ` +
                    `±${MAX_ABS_MARGIN_CM} cm range (possible cm/mm mix-up)`,
            };
        }
    }
    // An all-zero asymmetric margin is a clinical no-op (an identical copy),
    // not a structure operation — surface it rather than silently create a
    // pointless duplicate (CLAUDE.md: never proceed in a meaningless state).
    if (dirs.every(([, v]) => v === 0)) {
        return {
            kind: 'invalid',
            reason:
                'at least one direction must be non-zero (an all-zero ' +
                'asymmetric margin would not change the structure)',
        };
    }

    const resolved = resolveSourceForNewStructure(
        input.roi,
        input.newRoi,
        deps.structures,
        'asymmetric margin',
        'expand or contract'
    );
    if (resolved.kind !== 'source') {
        return resolved;
    }

    // The six named patient directions → the two engine vectors, mapped
    // ONCE, here, each via the SAME single cm→mm choke-point. This is the
    // dominant-hazard boundary; the per-direction goldens
    // pin every slot+sign.
    const validated = {
        sourceRoiNumber: resolved.source.roiNumber,
        sourceVolumeCm3: resolved.source.volumeCm3,
        negLpsMm: [
            cmToMmChokepoint(input.rightCm),
            cmToMmChokepoint(input.anteriorCm),
            cmToMmChokepoint(input.inferiorCm),
        ] as LpsMarginMm,
        posLpsMm: [
            cmToMmChokepoint(input.leftCm),
            cmToMmChokepoint(input.posteriorCm),
            cmToMmChokepoint(input.superiorCm),
        ] as LpsMarginMm,
        newRoi: input.newRoi,
    } as AsymMarginValidated;
    return { kind: 'validated', validated };
}

/* ── Stage 2: preview (compute geometry ONCE) ── */

interface AsymMarginPreviewedData {
    readonly computed: ComputedGeometry;
    readonly newRoi: string;
    readonly volumeDeltaCm3: number;
}
/** Only `previewAsymmetricMargin` mints this; `commitAsymmetricMargin`
 *  accepts only this, so the applied geometry IS the previewed geometry
 *  (no recompute path). */
export type AsymMarginPreviewed = Brand<
    AsymMarginPreviewedData,
    'AsymMarginPreviewed'
>;

export type AsymMarginPreviewResult =
    | { readonly kind: 'previewed'; readonly previewed: AsymMarginPreviewed }
    | { readonly kind: 'failed'; readonly reason: string };

export async function previewAsymmetricMargin(
    validated: AsymMarginValidated,
    deps: AsymMarginDeps
): Promise<AsymMarginPreviewResult> {
    const r = await previewSingleCall(
        validated,
        (v) =>
            deps.geometry.applyAsymmetricMargin(
                v.sourceRoiNumber,
                v.negLpsMm,
                v.posLpsMm
            ),
        deps.geometry.releaseComputed,
        'the resulting structure would be empty'
    );
    return r.kind === 'failed'
        ? r
        : { kind: 'previewed', previewed: r.previewed as AsymMarginPreviewed };
}

/* ── Stage 3: commit (write the ALREADY-computed geometry to a NEW ROI) ── */

export async function commitAsymmetricMargin(
    previewed: AsymMarginPreviewed,
    deps: AsymMarginDeps
): Promise<CommitResult> {
    return commitComputed(previewed, deps.commit.createNewStructure);
}
